import { useRef, useEffect } from "react";
import { UIMsg } from "../types";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";

interface TranscriptProps {
    currentMessages: UIMsg[];
    lastText: string;
    aiReplyText: string;
    isUserSpeaking: boolean;
    status: string; // 'recognizing' | 'idle' | etc
    conversationActive: boolean;
    showLiveCaptions: boolean;
    onScrollToBottom?: () => void;
    topOffset?: string;
    height?: string;
    /** Extra bottom padding inside the scroll area (e.g. when type-chat composer is visible) */
    messagesBottomPaddingClass?: string;
}

export function Transcript({
    currentMessages,
    lastText,
    aiReplyText,
    isUserSpeaking,
    status,
    conversationActive,
    showLiveCaptions,
    topOffset = '20vh',
    height = '45vh',
    messagesBottomPaddingClass = 'pb-28',
}: TranscriptProps) {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';
    const messagesScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = messagesScrollRef.current;
        if (el) {
            try { el.scrollTop = el.scrollHeight; } catch { }
        }
    }, [currentMessages, lastText, aiReplyText, isUserSpeaking, conversationActive]);

    // If captions hidden
    if (conversationActive && !showLiveCaptions) {
        return (
            <div className={`absolute left-0 right-0 z-30 flex items-center justify-center text-sm ${isDark ? 'text-white/70' : 'text-gray-600'
                }`} style={{ top: topOffset, height }}>
                {t('whisper.cc.hidden')}
            </div>
        );
    }

    // If active or showing transcript
    if (conversationActive || (!conversationActive && showLiveCaptions)) {
        return (
            <div className="absolute left-0 right-0 z-30" style={{ top: topOffset, height }}>
                <div
                    ref={messagesScrollRef}
                    className="max-w-xl mx-auto px-4 h-full overflow-y-auto"
                    style={{
                        maskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
                    }}
                >
                    <div className={`flex flex-col gap-3 pt-4 ${messagesBottomPaddingClass}`}>
                        {currentMessages.map((m) => (
                            <div
                                key={m.id}
                                className={[
                                    "max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-lg backdrop-blur-sm",
                                    m.role === "user"
                                        ? isDark
                                            ? "self-end bg-white/20 text-white border border-white/20"
                                            : "self-end bg-white/50 text-gray-800 border border-white/60"
                                        : isDark
                                            ? "self-start bg-[#3d5278]/80 text-white border border-white/10"
                                            : "self-start bg-white/40 text-gray-800 border border-white/50",
                                ].join(" ")}
                            >
                                <div>{m.text}</div>
                            </div>
                        ))}

                        {/* Realtime User Text */}
                        {status === "recognizing" && lastText && (
                            <div className={`max-w-[80%] self-end rounded-2xl px-4 py-2 text-sm shadow-lg backdrop-blur-sm border italic ${isDark
                                ? 'bg-white/10 text-white/80 border-white/20'
                                : 'bg-white/30 text-gray-700 border-white/50'
                                }`}>
                                <div>{lastText}</div>
                            </div>
                        )}

                        {/* Thinking Indicator (Pulse/Bounce) */}
                        {status === "recognizing" && !isUserSpeaking && !aiReplyText && lastText && (
                            <div className={`max-w-[80%] self-start rounded-2xl px-4 py-2 text-sm shadow-lg backdrop-blur-sm border ${isDark
                                ? 'bg-white/20 text-white border-white/30'
                                : 'bg-white/40 text-gray-800 border-white/60'
                                }`}>
                                <div className="flex items-center gap-1 h-5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        )}

                        {/* Speaking Indicator (... dots) */}
                        {status === "recognizing" && isUserSpeaking && !lastText && (
                            <div className={`max-w-[80%] self-end rounded-2xl px-4 py-2 text-sm shadow-lg backdrop-blur-sm border ${isDark
                                ? 'bg-white/10 text-white/80 border-white/20'
                                : 'bg-white/30 text-gray-700 border-white/50'
                                }`}>
                                <div className="flex items-center gap-1 h-5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        )}

                        {/* AI Realtime Reply */}
                        {aiReplyText && (
                            <div className={`max-w-[80%] self-start rounded-2xl px-4 py-2 text-sm shadow-lg backdrop-blur-sm border ${isDark
                                ? 'bg-white/20 text-white border-white/30'
                                : 'bg-white/40 text-gray-800 border border-white/60'
                                }`}>
                                <div>{aiReplyText}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
